import { IBusLocation, IBusStopEvent } from "@/domain/entities/lineInfo";

import { useLineStore } from "@/stores/lineStore";
import {
  AllocStatusCode,
  ArrivalTimeOptionCode,
  BusStopEventCode,
} from "@/types";
import { useCachedArrivalTime } from "./useCachedArrivalTime";
import {
  IArrivalTime,
  ICachedArrivalTime,
} from "@/domain/entities/lineInfo/arrivalTime.types";
import { IResultData } from "@/domain/entities/shared/result";
import { ref } from "vue";

export const useTmapExpectedArrivalTimes = (busStopCount: number) => {
  const MAX_CACHED_COUNT = 20;
  const CACHE_EXPIRY_SECONDS = 60;

  const DUMMY_ARRIVAL_TIMES = Array.from({ length: busStopCount }, () => ({
    type: ArrivalTimeOptionCode.TMAP,
    time: "",
    prefix: "",
    busStopUid: "",
  }));

  const lineStore = useLineStore();

  const tmapArrivalTimes = ref<IArrivalTime[]>([...DUMMY_ARRIVAL_TIMES]);

  const {
    cachedArrivalTimes: cachedTmapArrivalTimes,
    refreshCachedArrivalTimes,
    getCachedArrivalTime,
    removeCachedArrivalTime,
    isFresh,
  } = useCachedArrivalTime(MAX_CACHED_COUNT, CACHE_EXPIRY_SECONDS);

  /** TMAP예상도착시각 구하는 함수*/
  const fetchTmapExpectationArrivalTimes = async (
    busLocations: IBusLocation[],
    allocUid: string,
    isInsideShuttle: boolean
  ): Promise<IResultData<IArrivalTime[]>> => {
    if (isInsideShuttle) {
      return {
        isSuccess: false,
        data: [...DUMMY_ARRIVAL_TIMES],
        errorMessage: "사내 셔틀은 Tmap예상도착시각을 이용할 수 없습니다.",
      };
    }

    const busLocation = busLocations.find((loc) => loc.allocUid === allocUid);

    if (!busLocation || !isBusDriving(busLocation)) {
      return {
        isSuccess: false,
        data: [...DUMMY_ARRIVAL_TIMES],
        errorMessage: "현재 해당 버스는 운행중이지 않습니다.",
      };
    }

    // 캐싱된 것이 있으면 1분이 넘었는지 TimeStamp 확인
    if (isFresh(allocUid))
      return {
        isSuccess: true,
        data: getCachedArrivalTime(allocUid)?.data ?? [...DUMMY_ARRIVAL_TIMES],
      };

    const busStopEvents = busLocation?.busStopEvents ?? [];
    const guideStartIndex = getGuideStartIndex(busStopEvents);

    // 버스가 마지막 정류장까지 지나간 경우
    if (guideStartIndex === -1) {
      refreshCachedArrivalTimes(
        matchTmapArrivalTime([], busStopEvents),
        allocUid
      );
      return {
        isSuccess: true,
        data: getCachedArrivalTime(allocUid)?.data ?? [...DUMMY_ARRIVAL_TIMES],
      };
    }

    const requestParams: IBusLocation = {
      ...busLocation!,
      busStopEvents: busStopEvents.slice(guideStartIndex),
    };

    const result = await lineStore.createTmapMultipleViaPointGuide(
      requestParams
    );

    handleUpdateCache(result, guideStartIndex, busStopEvents, allocUid);

    return {
      ...result,
      data: getCachedArrivalTime(allocUid)?.data ?? [...DUMMY_ARRIVAL_TIMES],
    };
  };

  /** API호출 결과에 따라 캐시 업데이트 */
  const handleUpdateCache = (
    result: IResultData<IArrivalTime[]>,
    guideStartIndex: number,
    busStopEvents: IBusStopEvent[],
    allocUid: string
  ) => {
    if (!result.isSuccess) return;

    const guideStartBusStopEvent = busStopEvents[guideStartIndex];

    const tmapArrivalTimes = result.data ?? [];

    const nextGuideBusStopEvent = busStopEvents[guideStartIndex + 1];
    const isNextBusStopEventEnter =
      nextGuideBusStopEvent?.busStopEventCode === BusStopEventCode.Enter;

    const isBusStopEventLeave =
      guideStartBusStopEvent.busStopEventCode === BusStopEventCode.Leave ||
      (guideStartBusStopEvent.collectedTs && // collectedTs가 존재한다면 busLocation API Response에 담긴 EventCode는 LEAVE다. (busLocationParser가 가장 최근 Event를 제외한 이전 Event를 다 None처리함.)
        guideStartBusStopEvent.busStopEventCode === BusStopEventCode.None);

    removeCachedArrivalTime(allocUid);
    refreshCachedArrivalTimes(
      matchTmapArrivalTime(
        isNextBusStopEventEnter || isBusStopEventLeave
          ? tmapArrivalTimes.slice(1)
          : tmapArrivalTimes,
        busStopEvents
      ),
      allocUid
    );

    updateTmapArrivalTimes(allocUid, cachedTmapArrivalTimes.value);
  };

  /** 주행안내 시작할 버스 정류장 index 구하는 함수 */
  const getGuideStartIndex = (busStopEvents: IBusStopEvent[]) => {
    let guideStartIndex = 0;

    const eventIndex = busStopEvents.findIndex(
      (event) => event.busStopEventCode !== BusStopEventCode.None
    );

    const busStopEventCode =
      busStopEvents[eventIndex]?.busStopEventCode ?? BusStopEventCode.None;

    // Event가 안찍힌 경우 (첫번째 정류장도 통과하지 못한 경우)
    if (eventIndex === -1) {
      guideStartIndex = 0;
      return guideStartIndex;
    }

    // 마지막 정류장을 떠난 경우
    if (busStopEventCode === BusStopEventCode.LastLeave) {
      return -1;
    }

    // 버스가 정류장에 진입한 경우
    if (
      busStopEventCode === BusStopEventCode.Enter ||
      busStopEventCode === BusStopEventCode.FirstEnter
    ) {
      guideStartIndex = eventIndex === 0 ? 0 : eventIndex - 1;
    }

    // 버스가 중간 정류장을 떠난 경우 or 버스가 정류장에 도착한 경우
    if (
      busStopEventCode === BusStopEventCode.Leave ||
      busStopEventCode === BusStopEventCode.Arrive
    ) {
      guideStartIndex = eventIndex;
    }

    return guideStartIndex;
  };

  /** tmapArrivalTime데이터들을 버스 정류장 갯수에 맞게 매칭시키는 함수 */
  const matchTmapArrivalTime = (
    timeInfos: IArrivalTime[],
    busStopEvents: IBusStopEvent[]
  ) => {
    if (!busStopEvents.length) return [];

    const matchedTimes: IArrivalTime[] = Array.from(
      { length: busStopEvents.length },
      () => ({
        type: ArrivalTimeOptionCode.TMAP,
        busStopUid: "",
        time: "",
        prefix: "",
      })
    );

    if (!timeInfos.length) return timeInfos;

    for (const timeInfo of timeInfos) {
      const index = busStopEvents.findIndex(
        (busStopEvent) => busStopEvent.uid === timeInfo.busStopUid
      );

      if (index !== -1) {
        matchedTimes[index] = timeInfo;
      }
    }

    return matchedTimes;
  };

  /**  */
  const updateTmapArrivalTimes = (
    allocUid: string,
    cachedArrivalTimes: ICachedArrivalTime[]
  ) => {
    const target = cachedArrivalTimes.find((cached) => cached.key === allocUid);

    tmapArrivalTimes.value = target
      ? target.data
      : Array.from({ length: busStopCount }, () => ({
          type: ArrivalTimeOptionCode.TMAP,
          time: "",
          prefix: "",
          busStopUid: "",
        }));
  };

  /** 현재 버스가 운행 중인지 체크하는 함수 */
  const isBusDriving = (busLocation: IBusLocation) => {
    if (busLocation.allocStatusCode === AllocStatusCode.DRIVEN) {
      return false;
    }

    const lastBusStopEvent =
      busLocation.busStopEvents[busLocation.busStopEvents.length - 1];
    if (
      lastBusStopEvent?.busStopEventCode === BusStopEventCode.LastLeave ||
      lastBusStopEvent?.busStopEventCode === BusStopEventCode.Arrive
    ) {
      return false;
    }

    const isBusStopEventExisted = busLocation.busStopEvents.some(
      (event) =>
        event.busStopEventCode !== BusStopEventCode.None &&
        event.busStopEventCode !== BusStopEventCode.LastLeave
    );

    if (isBusStopEventExisted) {
      return true;
    }

    return false;
  };

  return {
    tmapArrivalTimes,
    fetchTmapExpectationArrivalTimes,
  };
};
